import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { WebEntryLayout } from './entryHelpers';
import { patientAuthFlow, isDemoAuthEnabled } from '../../auth/patientAuthFlow';

export default function LoginScreen() {
  const navigate = useNavigate();
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [otpRequestId, setOtpRequestId] = useState<string | undefined>();
  /** The code checked out, but no NeoHealthCard account uses this number. */
  const [noAccount, setNoAccount] = useState(false);
  const [devCode, setDevCode] = useState<string | undefined>();
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const next = [...otp];
    next[index] = value;
    setOtp(next);
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const routeByStatus = (status: string) => {
    if (status === 'approved') {
      navigate('/patient', { replace: true });
    } else if (status === 'rejected' || status === 'need_update') {
      navigate('/patient/verification-update-required', { replace: true });
    } else {
      navigate('/patient/approval-pending', { replace: true });
    }
  };

  const handleSendOtp = async () => {
    setError('');
    patientAuthFlow.setLoginMethod('phone');

    if (!phone.trim()) {
      setError('Enter your phone number.');
      return;
    }

    setBusy(true);
    const sent = await patientAuthFlow.backendRequestOtp('phone', phone.trim());
    setBusy(false);

    if (sent.success) {
      setNoAccount(false);
      setOtpRequestId(sent.otpRequestId);
      // Only ever populated when VITE_ENABLE_DEMO_AUTH=true (see isDemoAuthEnabled) —
      // shown so local testing doesn't require digging through backend logs.
      setDevCode(sent.demoOtp);
      return;
    }

    // Say it whether or not demo auth is on: a swallowed failure looks
    // exactly like a code that is on its way.
    setError(sent.error
      ? `Could not send the code: ${sent.error}`
      : 'Unable to reach the server. Please check your connection and try again.');
  };

  const handleVerify = async () => {
    setError('');
    const code = otp.join('');

    if (!phone.trim() || code.length !== 6) {
      setError('Enter your phone number and the 6-digit code.');
      return;
    }

    if (otpRequestId) {
      setBusy(true);
      const verified = await patientAuthFlow.backendVerifyOtp({ otpRequestId, code });
      setBusy(false);
      if (verified.success && verified.patient) {
        patientAuthFlow.loginVerifyOTP();
        routeByStatus(verified.approvalStatus ?? 'approved');
        return;
      }
      // The code was RIGHT but this number has no account. Saying "check the
      // code" sent people round the loop: the code is spent by now, so every
      // retry answered "this code has expired". Send them to registration.
      if (verified.success && verified.noAccount) {
        setNoAccount(true);
        setError('');
        return;
      }
      // Backend reachable but code wrong / no account — show the REAL error. Never
      // fall back to local-only login here: the backend has already ruled on this code.
      setError(verified.error ?? 'OTP verification failed — check the code and try again.');
      return;
    }

    // No otpRequestId means the OTP was never sent (backend unreachable) — only
    // continue with the local-only preview flow when explicitly enabled for
    // this environment (VITE_ENABLE_DEMO_AUTH=true).
    if (!isDemoAuthEnabled()) {
      setError('Please request the OTP first.');
      return;
    }
    patientAuthFlow.loginVerifyOTP();
    const status = patientAuthFlow.checkApprovalAfterLogin();
    routeByStatus(status);
  };

  return (
    <WebEntryLayout>
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: '48px 28px 40px',
        fontFamily: T.font,
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      <div style={{ fontSize: 22, fontWeight: 800, color: T.text, textAlign: 'center' }}>
        Phone Login
      </div>
      <div style={{ fontSize: 13, color: T.textSec, textAlign: 'center', marginTop: 6 }}>
        Sign in with your phone number & OTP
      </div>

      {/* Phone input */}
      <div style={{ display: 'flex', width: '100%', maxWidth: 340, marginTop: 28, borderRadius: 12, border: `1px solid ${T.border}`, overflow: 'hidden' }}>
        <div
          style={{
            padding: '12px 14px',
            background: T.ice,
            borderRight: `1px solid ${T.border}`,
            fontSize: 13,
            fontWeight: 600,
            color: T.text,
            fontFamily: T.font,
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          +91
        </div>
        <input
          type="tel"
          inputMode="numeric"
          maxLength={10}
          placeholder="98765 43210"
          value={phone}
          onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
          style={{
            flex: 1,
            border: 'none',
            outline: 'none',
            padding: '12px 14px',
            fontSize: 14,
            fontFamily: T.font,
            color: T.text,
            background: T.white,
            minWidth: 0,
          }}
        />
      </div>

      {/* Send / resend OTP */}
      <div
        onClick={busy ? undefined : handleSendOtp}
        style={{
          fontSize: 11,
          color: T.teal,
          fontWeight: 600,
          cursor: busy ? 'default' : 'pointer',
          fontFamily: T.font,
          marginTop: 10,
        }}
      >
        {otpRequestId ? 'Resend OTP' : 'Send OTP'}
      </div>

      {devCode && (
        <div style={{
          fontSize: 10, color: T.warning, fontFamily: T.mono, fontWeight: 700,
          marginTop: 10, padding: '4px 10px', background: `${T.warning}14`,
          borderRadius: 8, border: `1px solid ${T.warning}40`,
        }}>
          DEV MODE — code is {devCode}
        </div>
      )}

      {/* OTP boxes */}
      <div style={{ display: 'flex', gap: 8, marginTop: 20, width: '100%', maxWidth: 340, justifyContent: 'center' }}>
        {otp.map((digit, i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleOtpChange(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
            style={{
              width: 44,
              height: 52,
              borderRadius: 10,
              border: digit ? `1.5px solid ${T.teal}` : `1.5px solid ${T.border}`,
              background: digit ? `${T.teal}0A` : T.white,
              textAlign: 'center',
              fontSize: 20,
              fontWeight: 700,
              fontFamily: T.mono,
              color: T.text,
              outline: 'none',
              boxSizing: 'border-box',
              flexShrink: 0,
            }}
          />
        ))}
      </div>

      {/* Error state (real backend errors are shown, never faked) */}
      {error && (
        <div style={{ fontSize: 12, color: T.danger, marginTop: 14, maxWidth: 340, textAlign: 'center', fontFamily: T.font }}>
          {error}
        </div>
      )}

      {/* Verify button */}
      <button
        disabled={busy}
        onClick={handleVerify}
        style={{
          width: '100%',
          maxWidth: 340,
          padding: '15px 0',
          background: `linear-gradient(135deg, ${T.teal}, ${T.overlap})`,
          color: T.white,
          border: 'none',
          borderRadius: 14,
          fontSize: 15,
          fontWeight: 700,
          fontFamily: T.font,
          cursor: 'pointer',
          marginTop: 24,
          marginBottom: 16,
          boxShadow: `0 4px 14px ${T.teal}40`,
        }}
      >
        {busy ? 'Please wait…' : 'Verify & Login'}
      </button>

      {/* Register link */}
      {/* Switch to email */}
      <div
        onClick={() => navigate('/patient/login/email')}
        style={{ fontSize: 11, color: T.teal, fontWeight: 600, cursor: 'pointer', fontFamily: T.font, marginBottom: 10 }}
      >
        Sign in with Email instead
      </div>

      {/* Verified, but there is no account on this number */}
      {noAccount && (
        <div
          style={{
            background: `${T.teal}0D`,
            border: `1px solid ${T.teal}40`,
            borderRadius: 12,
            padding: '12px 14px',
            marginBottom: 12,
            fontFamily: T.font,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: T.text, marginBottom: 4 }}>
            Number verified — but there is no account yet
          </div>
          <div style={{ fontSize: 12, color: T.textSec, lineHeight: 1.5, marginBottom: 10 }}>
            {phone.trim()} is not registered with NeoHealthCard. Create an account to continue; your
            number is already verified.
          </div>
          <button
            onClick={() => navigate('/patient/onboarding')}
            style={{
              width: '100%',
              padding: '10px 0',
              borderRadius: 10,
              border: 'none',
              background: T.teal,
              color: T.white,
              fontSize: 13,
              fontWeight: 700,
              fontFamily: T.font,
              cursor: 'pointer',
            }}
          >
            Register with NeoHealthCard
          </button>
        </div>
      )}

      {/* Forgot password — a password account can be recovered from here too */}
      <div
        onClick={() => navigate('/patient/login/forgot')}
        style={{
          fontSize: 12,
          color: T.teal,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: T.font,
          textAlign: 'center',
          marginBottom: 10,
        }}
      >
        Forgot your password?
      </div>

      {/* Register link */}
      <div
        onClick={() => navigate('/patient/onboarding')}
        style={{
          fontSize: 13,
          color: T.teal,
          fontWeight: 600,
          cursor: 'pointer',
          fontFamily: T.font,
          textAlign: 'center',
        }}
      >
        New? Register with NeoHealthCard
      </div>
    </div>
    </WebEntryLayout>
  );
}
